import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import ModalPortal from "../../../components/Modal/ModalPortal"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import InputField from "../../../components/InputField/InputField"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { faClipboardList, faClose, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import GetSpace from "../../../utils/space"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { useError } from "../../../context/useErrorContext"
import type { SaveChecklistTemplateStep } from "../../../Objects/Checklist/SaveChecklistTemplateStep"
import { employeeMemory } from "../../../Objects/Memory/Memory"

interface Props {
  step: SaveChecklistTemplateStep | null
  onSave: (step: SaveChecklistTemplateStep) => void
  onClose: () => void
}

const emptyStep = (): SaveChecklistTemplateStep => ({
  order: 0,
  title: "",
  description: null,
  responseType: "Confirmation",
  isRequired: true,
  responsibleEmployeeId: null,
  options: [],
})

export const ChecklistTemplateStepPopup: React.FC<Props> = ({ step, onSave, onClose }) => {
  const { t } = useTranslation(["pages", "general"])

  const { setError } = useError()

  const [form, setForm] = useState<SaveChecklistTemplateStep>(step ?? emptyStep())

  const [newOptionLabel, setNewOptionLabel] = useState("")

  const responseTypeOptions = [
    { label: t("checklists.templates.responseTypeConfirmation"), value: "Confirmation" },
    { label: t("checklists.templates.responseTypeYesNo"), value: "YesNo" },
    { label: t("checklists.templates.responseTypeMultipleChoice"), value: "MultipleChoice" },
    { label: t("checklists.templates.responseTypeText"), value: "Text" },
    { label: t("checklists.templates.responseTypeNumber"), value: "Number" },
    { label: t("checklists.templates.responseTypeDateTime"), value: "DateTime" },
  ]

  const employeeOptions = [
    { label: t("checklists.templates.noResponsible"), value: "" },
    ...employeeMemory.listData.map((employee) => ({ label: employee.userName, value: employee.id })),
  ]

  const addOption = () => {
    if (!newOptionLabel.trim()) return

    setForm((prev) => ({
      ...prev,
      options: [...prev.options, { label: newOptionLabel.trim(), order: prev.options.length }],
    }))

    setNewOptionLabel("")
  }

  const removeOption = (index: number) =>
    setForm((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }))

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (!form.title.trim()) {
      setError(t("general:label_title_erro"), t("checklists.templates.stepTitlePlaceholder"))
      return
    }

    if (form.responseType === "MultipleChoice" && form.options.length < 2) {
      setError(t("general:label_title_erro"), t("checklists.templates.multipleChoiceOptionsError"))
      return
    }

    onSave(form)
    onClose()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={step ? t("checklists.templates.editStepTitle") : t("checklists.templates.createStepTitle")}
              icon={faClipboardList}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.button.onClose"
                  />
                </div>
              }
            />

            <form onSubmit={handleSubmit} className="p-2">
              <InputField
                type="text"
                icon={faClipboardList}
                value={form.title}
                placeholder={t("checklists.templates.stepTitlePlaceholder")}
                onInvalidMessage={t("checklists.templates.stepTitlePlaceholder")}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                required
                maxLength={200}
                descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.input.title"
              />

              <GetSpace top={10} />

              <InputField
                type="text"
                value={form.description ?? ""}
                placeholder={t("checklists.templates.stepDescriptionPlaceholder")}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                maxLength={500}
                descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.input.description"
              />

              <div className={defaultStyle.divisionBar}></div>

              <div className="gap-2 space-y-2">
                <GenericDropDown
                  data={responseTypeOptions}
                  initialState={form.responseType}
                  handlerFuncion={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      responseType: e.target.value as SaveChecklistTemplateStep["responseType"],
                    }))
                  }
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.select.responseType"
                />

                <GenericDropDown
                  data={employeeOptions}
                  initialState={form.responsibleEmployeeId ?? ""}
                  handlerFuncion={(e) =>
                    setForm((prev) => ({ ...prev, responsibleEmployeeId: e.target.value || null }))
                  }
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.select.responsible"
                />
              </div>

              <GetSpace top={10} />

              <SimpleToggle
                title={t("checklists.templates.stepRequired")}
                value={form.isRequired}
                onChange={(value) => setForm((prev) => ({ ...prev, isRequired: value }))}
                descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.toggle.required"
              />

              {form.responseType === "MultipleChoice" && (
                <>
                  <div className={defaultStyle.divisionBar}></div>

                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <InputField
                        type="text"
                        value={newOptionLabel}
                        onChange={(e) => setNewOptionLabel(e.target.value)}
                        placeholder={t("checklists.templates.newOptionPlaceholder")}
                        maxLength={150}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.input.newOption"
                      />
                    </div>

                    <ButtonIcon
                      sizeNumber={40}
                      bgColor={THEME.colors.buttonBGPlusColor}
                      icon={faPlus}
                      onClick={addOption}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.button.addOption"
                    />
                  </div>

                  {form.options.length > 0 && (
                    <div className="flex flex-col gap-1.5 mt-2 max-h-[30vh] overflow-y-auto">
                      {form.options.map((option, index) => (
                        <div
                          key={`${option.label}-${index}`}
                          className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg bg-gray-50 border border-gray-200"
                        >
                          <span className="text-sm text-gray-700">{option.label}</span>
                          <ButtonIcon
                            sizeNumber={28}
                            bgColor={THEME.colors.buttonBGRemoveColor}
                            icon={faTrash}
                            onClick={() => removeOption(index)}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.button.removeOption"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <Button
                  label={t("general:label_save")}
                  typeButton="submit"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplateStepPopup.button.save"
                />
              </div>
            </form>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
